import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useElementSize } from './useElementSize'
import { classifyDensity, type WidgetDensity } from './density'

export interface WidgetDensityValue {
  density: WidgetDensity
  width: number
  height: number
}

/** Default context value — `full`, unmeasured. Every widget primitive (`WidgetFrame`, `ChartFrame`,
 * `KpiTile`, `GaugeTile`, the `charts/*` wrappers) reads this via `useWidgetDensity()`; outside a
 * `WidgetDensityProvider` (every screen this story does not touch — the portfolio/jurisdictions/
 * operations analytics panels, every other widget test in the repo) it resolves to exactly this
 * value, so none of them change behavior. */
const DEFAULT_VALUE: WidgetDensityValue = { density: 'full', width: 0, height: 0 }

const WidgetDensityContext = createContext<WidgetDensityValue>(DEFAULT_VALUE)

export function useWidgetDensity(): WidgetDensityValue {
  return useContext(WidgetDensityContext)
}

export interface WidgetDensityProviderProps {
  children: ReactNode
}

/**
 * Measures its own rendered box (`useElementSize`/`ResizeObserver`) and classifies it
 * (`classifyDensity`) — the "self-adjusting" mechanism `DashboardGrid` wraps around every grid cell
 * so its widget can adapt its own level of detail to how much room it actually got, live, on every
 * resize (window resize, list-pane collapse, breakpoint change) — no page reload, no manual
 * "compact/comfortable" toggle required for this specific behavior (design-system.md §5).
 */
export function WidgetDensityProvider({ children }: WidgetDensityProviderProps) {
  const [ref, size] = useElementSize<HTMLDivElement>()
  const value = useMemo<WidgetDensityValue>(
    () => ({ density: classifyDensity(size.width, size.height), width: size.width, height: size.height }),
    [size.width, size.height],
  )
  return (
    <div ref={ref} className="iw-dashgrid-cell__measure">
      <WidgetDensityContext.Provider value={value}>{children}</WidgetDensityContext.Provider>
    </div>
  )
}
